import { StreamMessageSchema } from '@baseline/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { apiResponse } from '../api/client.js';
import { keys } from '../api/queries.js';
import { useToast } from '../components/Toast.js';

import type { StreamMessage } from '@baseline/shared';

// Live updates from `GET /api/stream` (plan Phase 10). The gateway needs the bearer token,
// which EventSource can't send, so this reads the event stream with fetch. While it is down,
// queries poll every 15 s (CLAUDE.md section 7); it reconnects after the server's `retry`.

const DEFAULT_RETRY_MS = 5_000;
export const POLL_MS = 15_000;

let streamLive = false;

/** The query client's `refetchInterval`: poll only while the stream is down. */
export function pollInterval(): number | false {
  return streamLive ? false : POLL_MS;
}

/** Parses `text/event-stream` chunks into `data:` payloads. Exported for tests. */
export function createSseParser(onData: (data: string) => void, onRetry: (ms: number) => void) {
  let buffer = '';
  return (chunk: string) => {
    buffer += chunk.replace(/\r\n?/g, '\n');
    let end = buffer.indexOf('\n\n');
    while (end >= 0) {
      const block = buffer.slice(0, end);
      buffer = buffer.slice(end + 2);
      const data: string[] = [];
      for (const line of block.split('\n')) {
        if (line.startsWith(':')) continue;
        const colon = line.indexOf(':');
        const field = colon < 0 ? line : line.slice(0, colon);
        const value = colon < 0 ? '' : line.slice(colon + 1).replace(/^ /, '');
        if (field === 'data') data.push(value);
        else if (field === 'retry' && /^\d+$/.test(value)) onRetry(Number(value));
      }
      if (data.length) onData(data.join('\n'));
      end = buffer.indexOf('\n\n');
    }
  };
}

/** Whether the stream is connected; the query client polls while it isn't. */
export function useLiveUpdates(): boolean {
  const qc = useQueryClient();
  const toast = useToast();
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    const abort = new AbortController();
    let retryMs = DEFAULT_RETRY_MS;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const handle = (message: StreamMessage) => {
      if (message.type === 'toast') {
        toast(message.text);
        return;
      }
      void qc.invalidateQueries({ queryKey: ['alerts'] });
      void qc.invalidateQueries({ queryKey: ['drafts'] });
      void qc.invalidateQueries({ queryKey: keys.dashboard });
      void qc.invalidateQueries({ queryKey: keys.freshness });
    };

    const connect = async () => {
      try {
        const res = await apiResponse('/stream', { signal: abort.signal });
        const reader = res.body?.pipeThrough(new TextDecoderStream()).getReader();
        if (!reader) throw new Error('no stream body');
        setConnected(true);
        streamLive = true;
        // Anything that changed while disconnected shows up now.
        void qc.invalidateQueries();
        const parse = createSseParser(
          (data) => {
            let json: unknown;
            try {
              json = JSON.parse(data);
            } catch {
              return;
            }
            const parsed = StreamMessageSchema.safeParse(json);
            if (parsed.success) handle(parsed.data);
          },
          (ms) => {
            retryMs = ms;
          },
        );
        for (;;) {
          const { value, done } = await reader.read();
          if (done) break;
          parse(value);
        }
      } catch {
        // Dropped or refused: fall back to polling until the next attempt.
      }
      setConnected(false);
      if (streamLive) {
        streamLive = false;
        // Refetching restarts each query's 15 s polling (see `pollInterval`).
        void qc.invalidateQueries();
      }
      if (!abort.signal.aborted) timer = setTimeout(() => void connect(), retryMs);
    };

    void connect();
    return () => {
      abort.abort();
      clearTimeout(timer);
    };
  }, [qc, toast]);

  return connected;
}
